import { CSSVariablesResolver } from "@mantine/core";

import { colors } from "#app/theme/v2/colors";
import { fontSizes } from "#app/theme/v2/fontSizes";
import { fontWeights } from "#app/theme/v2/fontWeights";
import { lineHeights } from "#app/theme/v2/lineHeights";
import { radius } from "#app/theme/v2/radius";
import { shadows } from "#app/theme/v2/shadows";
import { spacing } from "#app/theme/v2/spacing";

const sanitizeCSSVariableKey = (key: string) =>
  key.replace(".", "-").replace(/([A-Z])/g, (c) => `-${c.toLowerCase()}`);

const flattenColors = (obj: Record<string, unknown>, prefix: string): [string, string][] =>
  Object.entries(obj).flatMap(([key, value]) => {
    const varName = `${prefix}-${key}`;
    return typeof value === "object" && value !== null
      ? flattenColors(value as Record<string, unknown>, varName)
      : [[varName, value as string]];
  });

const colorCSSVariables = Object.fromEntries(
  flattenColors(colors as unknown as Record<string, unknown>, "--fds-color"),
);

const spacingCSSVariables = Object.fromEntries(
  Object.entries(spacing).map(([k, v]) => [`--fds-spacing-${sanitizeCSSVariableKey(k)}`, v]),
);
const radiusCSSVariables = Object.fromEntries(
  Object.entries(radius).map(([k, v]) => [`--fds-radius-${sanitizeCSSVariableKey(k)}`, v]),
);
const shadowsCSSVariables = Object.fromEntries(
  Object.entries(shadows).map(([k, v]) => [`--fds-shadow-${sanitizeCSSVariableKey(k)}`, v]),
);
const fontSizesCSSVariables = Object.fromEntries(
  Object.entries(fontSizes).map(([k, v]) => [`--fds-font-size-${sanitizeCSSVariableKey(k)}`, v]),
);
const fontWeightsCSSVariables = Object.fromEntries(
  Object.entries(fontWeights).map(([k, v]) => [
    `--fds-font-weight-${sanitizeCSSVariableKey(k)}`,
    v,
  ]),
);
const lineHeightsCSSVariables = Object.fromEntries(
  Object.entries(lineHeights).map(([k, v]) => [
    `--fds-line-height-${sanitizeCSSVariableKey(k)}`,
    v,
  ]),
);

export const fdsCssVariables = {
  ...colorCSSVariables,
  ...spacingCSSVariables,
  ...radiusCSSVariables,
  ...shadowsCSSVariables,
  ...fontSizesCSSVariables,
  ...fontWeightsCSSVariables,
  ...lineHeightsCSSVariables,
};

export const cssVariablesResolver: CSSVariablesResolver = () => ({
  variables: fdsCssVariables,
  light: {},
  dark: {},
});
